import { Component, Input, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

/** The statutory reference behind each SDS tab. */
export type SdsLegalReferenceName = 'chemicals' | 'substitution' | 'exposure';

interface SdsLegalReference {
  readonly url: string;
  readonly tooltipKey: string;
  readonly titleKey: string;
  readonly subtitleKey: string;
}

/**
 * The references the tabs may point at, as compile-time constants.
 *
 * Framing a page means binding the iframe's src, which Angular classifies as a
 * RESOURCE_URL and refuses to interpolate — bypassSecurityTrustResourceUrl is
 * the only way to bind one, and there is no sanitiser for resource URLs to fall
 * back on. So the safety has to come from the value, not from Angular: callers
 * pass one of the keys below and the URL never leaves this file, which keeps it
 * out of reach of route params, API payloads and user input. isLovdataUrl()
 * re-checks the origin before the bypass so that even a careless edit here
 * cannot point the frame at a hostile host.
 */
const LEGAL_REFERENCES: { readonly [key in SdsLegalReferenceName]: SdsLegalReference; } = {
  // Forskrift om utførelse av arbeid § 2-1 — krav om stoffkartotek.
  chemicals: {
    url: 'https://lovdata.no/forskrift/2011-12-06-1357/%C2%A72-1',
    tooltipKey: 'SDS_LEGAL_CHEMICALS_TOOLTIP',
    titleKey: 'SDS_LEGAL_CHEMICALS_TITLE',
    subtitleKey: 'SDS_LEGAL_CHEMICALS_SUBTITLE'
  },
  // Produktkontrolloven § 3a — substitusjonsplikt.
  substitution: {
    url: 'https://lovdata.no/lov/1976-06-11-79/%C2%A73a',
    tooltipKey: 'SDS_LEGAL_SUBST_TOOLTIP',
    titleKey: 'SDS_LEGAL_SUBST_TITLE',
    subtitleKey: 'SDS_LEGAL_SUBST_SUBTITLE'
  },
  // Forskrift om utførelse av arbeid kapittel 31, § 31-2 — register over eksponerte arbeidstakere.
  exposure: {
    url: 'https://lovdata.no/dokument/SF/forskrift/2011-12-06-1357/KAPITTEL_6-1#%C2%A731-2',
    tooltipKey: 'SDS_LEGAL_EXPOSURE_TOOLTIP',
    titleKey: 'SDS_LEGAL_EXPOSURE_TITLE',
    subtitleKey: 'SDS_LEGAL_EXPOSURE_SUBTITLE'
  }
};

/** The one host this component will ever frame. */
const LOVDATA_ORIGIN = 'https://lovdata.no';

/**
 * The shared "§" action on the SDS tabs: a small button that opens the statutory
 * text behind that tab, framed straight from Lovdata.
 *
 * Each tab names its own reference — the Chemicals tab the duty to keep a
 * chemical inventory, Substitution assessment the substitution duty, the
 * Exposure register the register requirement — so the component only holds the
 * framing, zoom and fallback behaviour.
 */
@Component({
  selector: 'app-sds-legal-reference',
  templateUrl: './sds-legal-reference.component.html',
  styleUrls: ['./sds-legal-reference.component.css'],
  standalone: false
})
export class SdsLegalReferenceComponent implements OnDestroy {
  /** Which entry of LEGAL_REFERENCES this button opens. */
  @Input() reference: SdsLegalReferenceName = 'chemicals';

  /** Zoom stops offered by the −/+ controls; 1 = the page's own size. */
  private static readonly zoomSteps = [0.75, 0.9, 1, 1.15, 1.3, 1.5, 1.75];

  dialogVisible = false;
  loading = false;
  blocked = false;
  frameUrl: SafeResourceUrl | null = null;
  zoom = 1;
  private loadTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly sanitizer: DomSanitizer) {}

  ngOnDestroy(): void {
    this.clearTimer();
  }

  /** Falls back to the chemicals reference if a caller passes an unknown name. */
  get definition(): SdsLegalReference {
    return LEGAL_REFERENCES[this.reference] || LEGAL_REFERENCES.chemicals;
  }

  open(): void {
    const url = this.definition.url;

    this.zoom = 1;
    this.dialogVisible = true;

    // Refuse to frame anything but Lovdata: the modal then shows the
    // "open it on lovdata.no" notice instead of an untrusted page. Lovdata
    // serves frame-ancestors as report-only today, so the page frames fine;
    // that notice is also what the user gets the day they start enforcing it.
    if (!this.isLovdataUrl(url)) {
      this.frameUrl = null;
      this.loading = false;
      this.blocked = true;
      return;
    }

    this.blocked = false;
    this.loading = true;
    this.frameUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);

    // A cross-origin frame reports nothing we can read, so treat "still nothing
    // after 12s" as unreachable and point the user at lovdata.no instead.
    this.clearTimer();
    this.loadTimer = setTimeout(() => {
      if (this.loading) {
        this.loading = false;
        this.blocked = true;
      }
    }, 12000);
  }

  onFrameLoaded(): void {
    this.clearTimer();
    this.loading = false;
  }

  close(): void {
    this.clearTimer();
    this.dialogVisible = false;
    this.loading = false;
    this.blocked = false;
    // Drop the src so the frame stops loading once the modal is gone.
    this.frameUrl = null;
  }

  /** Escape hatch: the full document on lovdata.no, in a new tab. */
  openExternally(): void {
    const url = this.definition.url;
    if (this.isLovdataUrl(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  /**
   * Origin check by parse rather than by prefix — a prefix test would accept
   * credentials-in-authority forms such as https://lovdata.no@example.org/.
   */
  private isLovdataUrl(url: string): boolean {
    try {
      return new URL(url).origin === LOVDATA_ORIGIN;
    } catch {
      return false;
    }
  }

  /** Steps one stop along zoomSteps; +1 zooms in, -1 zooms out. */
  changeZoom(direction: number): void {
    const steps = SdsLegalReferenceComponent.zoomSteps;
    const next = steps.indexOf(this.zoom) + direction;
    if (next >= 0 && next < steps.length) {
      this.zoom = steps[next];
    }
  }

  resetZoom(): void {
    this.zoom = 1;
  }

  get canZoomIn(): boolean {
    const steps = SdsLegalReferenceComponent.zoomSteps;
    return this.zoom !== steps[steps.length - 1];
  }

  get canZoomOut(): boolean {
    return this.zoom !== SdsLegalReferenceComponent.zoomSteps[0];
  }

  get zoomLabel(): string {
    return `${Math.round(this.zoom * 100)}%`;
  }

  /**
   * Scales the framed page without shrinking the visible area: the frame is laid
   * out at the inverse of the zoom, then scaled back up from its top-left corner
   * (transform-origin comes from the stylesheet). Bound as separate primitives so
   * change detection compares strings rather than a freshly built style object.
   */
  get frameSize(): string {
    return `${100 / this.zoom}%`;
  }

  get frameTransform(): string {
    return `scale(${this.zoom})`;
  }

  private clearTimer(): void {
    if (this.loadTimer) {
      clearTimeout(this.loadTimer);
      this.loadTimer = null;
    }
  }
}
